import { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, Divider, Paper, IconButton } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Location from 'assets/images/icons/location.png';
import axios from 'axios';
import { apiHowen } from '../../../api/axiosClient';
import DetailDialog from '../../../components/DetailDialog';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { baseMaps } from '../../../utils/mapConfig';
import { useLanguage } from '../../../utils/i18n';
import { API_BASES, VSS_CONFIG } from '../../../config/constants';

const VSS_HOST = API_BASES.VSS;

export default function DetailReportFatiguePage({ open, onClose, data = null }) {
  const { t } = useLanguage();
  const [localData, setLocalData] = useState(data);
  const [howenToken, setHowenToken] = useState('');
  const [evidenceData, setEvidenceData] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const mapWrapperRef = useRef(null);

  const [isFullscreenMap, setIsFullscreenMap] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreenMap(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreenMap = () => {
    if (!document.fullscreenElement) {
      if (mapWrapperRef.current?.requestFullscreen) mapWrapperRef.current.requestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
  };

  useEffect(() => {
    setLocalData(data);
    setEvidenceData(null);
    setHowenToken('');
  }, [data, open]);
  console.log('Fatuge', localData);

  useEffect(() => {
    let mounted = true;
    if (!open) return;

    (async () => {
      try {
        const resp = await apiHowen.get('/get-token');
        const tokenData = resp?.data ?? resp;
        const token = tokenData?.data?.token ?? tokenData?.token ?? tokenData ?? '';
        if (!mounted) return;
        const tokStr = typeof token === 'string' ? token : JSON.stringify(token);
        setHowenToken(tokStr);
      } catch (e) {
        console.log(e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    let mounted = true;
    const alarmGuid = localData?.guid ?? localData?.alarmGuid ?? localData?.alarmId ?? null;

    if (!open || !alarmGuid || !howenToken) return;

    (async () => {
      try {
        const url = `${VSS_HOST}/record/evidenceToRetrieveByGuid.action?alarmId=${encodeURIComponent(
          alarmGuid
        )}&token=${encodeURIComponent(String(howenToken))}&scheme=http&lang=en_US`;

        try {
          const evidenceResp = await axios.post(url);
          const body = evidenceResp?.data ?? evidenceResp;
          if (!mounted) return;
          setEvidenceData(body?.data ?? body);
          return;
        } catch (axiosErr) {
          console.log(axiosErr);
        }

        try {
          const fetchResp = await fetch(url, { method: 'GET', mode: 'cors' });
          const contentType = fetchResp.headers.get('content-type') ?? '';
          if (contentType.includes('application/json')) {
            const body = await fetchResp.json();
            if (!mounted) return;
            setEvidenceData(body?.data ?? body);
          } else {
            if (mounted) setEvidenceData(null);
          }
          return;
        } catch (fetchErr) {
          console.log(fetchErr);
        }
      } catch (err) {
        console.log(err);
        if (mounted) setEvidenceData(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [localData, howenToken, open]);

  const parseOdo = () => {
    const d = localData;
    if (!d) return null;
    const tryFromParaJson = (obj) => {
      try {
        if (!obj) return null;
        const raw = typeof obj === 'string' ? obj : JSON.stringify(obj);
        const parsed = JSON.parse(raw);
        return parsed?.totalMileage ?? parsed?.total_mileage ?? null;
      } catch {
        return null;
      }
    };
    const candidates = [
      tryFromParaJson(d.paraJson),
      tryFromParaJson(d?._raw?.paraJson),
      d?.totalMileage ?? null,
      d?._raw?.totalMileage ?? null
    ];
    for (const c of candidates) if (c !== null && c !== undefined && c !== '') return c;
    return null;
  };

  const odoRaw = parseOdo();
  const odoDisplay = odoRaw == null ? '-' : (Number(odoRaw) / 1000).toFixed(2);

  const alarmFiles = useMemo(() => {
    const src = evidenceData ?? localData?.data ?? localData;
    if (!src) return [];
    if (Array.isArray(src.alarmFile)) return src.alarmFile;
    if (Array.isArray(src.alarmFileList)) return src.alarmFileList;
    return [];
  }, [evidenceData, localData]);

  const buildSrc = (f) => {
    if (!f) return null;
    let finalUrl = null;

    if (f.downUrl && typeof f.downUrl === 'string' && (f.downUrl.startsWith('http') || f.downUrl.startsWith('https'))) {
      finalUrl = f.downUrl;
    } else if (
      f.fileThumbnail &&
      typeof f.fileThumbnail === 'string' &&
      (f.fileThumbnail.startsWith('http') || f.fileThumbnail.startsWith('https'))
    ) {
      finalUrl = f.fileThumbnail;
    }

    if (finalUrl) {
      if (finalUrl.includes('http://103.169.73.227:9988') || finalUrl.includes(VSS_CONFIG.IP_URL)) {
        // Strip the port and replace with domain host if needed, or just replace the known IP block
        const changedUrl = finalUrl.replace(/http:\/\/[0-9.]+(:[0-9]+)?/g, `https://${VSS_CONFIG.HOST}`);
        return changedUrl;
      }
      return finalUrl;
    }

    return null;
  };

  useEffect(() => {
    if (!open || !mapRef.current) return;

    const timer = setTimeout(() => {
      if (!mapInstanceRef.current && mapRef.current) {
        const defaultMap = baseMaps['Google Map'] || baseMaps['OpenStreetMap'] || Object.values(baseMaps)[0];
        const map = L.map(mapRef.current, {
          zoomControl: true,
          attributionControl: false,
          layers: [defaultMap]
        }).setView([-6.2, 106.8], 10);
        L.control.layers(baseMaps, null, { position: 'topright' }).addTo(map);
        mapInstanceRef.current = map;
      }

      const map = mapInstanceRef.current;
      if (!map) return;

      map.invalidateSize();

      const endGps = (evidenceData?.endGps ?? localData?.endGps ?? localData?.alarmGps ?? '').trim();
      if (!endGps) {
        if (markerRef.current) {
          map.removeLayer(markerRef.current);
          markerRef.current = null;
        }
        return;
      }

      const parts = endGps.split(',').map((p) => p.trim());
      if (parts.length !== 2) return;

      const lon = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return;

      const latlng = [lat, lon];

      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      const icon = L.icon({
        iconUrl: Location,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      markerRef.current = L.marker(latlng, { icon }).addTo(map);
      markerRef.current.bindPopup(`<div style="font-size:14px">End GPS<br/>${lat}, ${lon}</div>`);
      map.setView(latlng, 15);
    }, 100);

    return () => clearTimeout(timer);
  }, [evidenceData, localData?.endGps, localData?.alarmGps, open]);

  useEffect(() => {
    if (!open && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
  }, [open]);

  const videos = alarmFiles
    .filter((f) => String(f.fileType) === '2')
    .map((f) => ({ src: buildSrc(f), meta: f }))
    .filter((x) => x.src);

  const images = alarmFiles
    .filter((f) => String(f.fileType) === '4' || String(f.fileType) === '3')
    .map((f) => ({ src: buildSrc(f), meta: f }))
    .filter((x) => x.src);

  return (
    <DetailDialog
      open={open}
      onClose={onClose}
      title={t('reportFatigue.alarmDetail', 'Alarm Detail')}
      subtitle={t('reportFatigue.fatigueReportInformation', 'Fatigue Report Information')}
      icon={<LocalPoliceIcon sx={{ color: '#fff', fontSize: 22 }} />}
      fullScreenMode={true}
    >
      <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 0, md: 2 } }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', md: 'row' }, mb: 3 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
              {videos.length > 0 ? (
                videos.map((v, idx) => (
                  <Box key={idx} sx={{ backgroundColor: '#000' }}>
                    <video controls style={{ width: '100%', display: 'block', objectFit: 'contain', height: 270 }}>
                      <source src={v.src} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                ))
              ) : (
                <Box sx={{ height: 270, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.noVideo', 'No video available')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
              {images.length > 0 ? (
                images.map((i, idx) => (
                  <Box key={idx} sx={{ backgroundColor: '#000' }}>
                    <img
                      src={i.src}
                      alt={`evidence-${idx}`}
                      style={{ width: '100%', display: 'block', objectFit: 'contain', height: 270 }}
                    />
                  </Box>
                ))
              ) : (
                <Box sx={{ height: 270, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.noImage', 'No image available')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper
              ref={mapWrapperRef}
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                height: isFullscreenMap ? '100vh' : 270,
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
              <IconButton
                onClick={toggleFullscreenMap}
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                }}
              >
                {isFullscreenMap ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Paper>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', minHeight: 300 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                {t('reportFatigue.vehicleInfo', 'Vehicle Info')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.licensePlate', 'License Plate')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localData?.vehicle?.license_plate ?? localData?.plateNo ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.imei', 'IMEI')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localData?.deviceno ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.vehicleType', 'Vehicle Type')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localData?.vehicle?.vehicle_type_name ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.odo', 'ODO')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {odoDisplay} Km
                  </Typography>
                </Box>
                {/* <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.shipmentNumber', 'Shipment Number')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localData?.vehicle?.shipment_number ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.shipmentInternal', 'Shipment Internal')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localData?.vehicle?.shipment_internal ?? '-'}
                  </Typography>
                </Box> */}
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', minHeight: 300 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                {t('reportFatigue.details', 'Details')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.alarmType', 'Alarm Type')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {evidenceData?.alarmTypeValue ?? localData?.alarmTypeValue ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.speed', 'Speed')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {String(evidenceData?.speed ?? localData?.speed ?? localData?.spds ?? '-')}{' '}
                    {(evidenceData?.speed ?? localData?.speed ?? localData?.spds) != null ? 'Km/h' : ''}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.time', 'Time')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {evidenceData?.alarmTime ?? localData?.createtime ?? localData?.reportTime ?? localData?.alarmTime ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('reportFatigue.alarmLocation', 'Alarm Location')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {evidenceData?.endGps ?? localData?.endGps ?? localData?.alarmGps ?? '-'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </DetailDialog>
  );
}
